import { Component, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ParametresService } from '../../services/requests/parametres.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-parametrages',
  templateUrl: './parametrages.component.html',
  styleUrls: ['./parametrages.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ParametragesComponent implements OnInit, OnDestroy, AfterViewInit {
  parametrages: any[] = [];
  isLoading = false;
  fieldTags: { [key: number]: string[] } = {}; 
  fieldInputs: { [key: number]: string } = {};
  selectedParamFilter: 'global' | 'pre' | 'alert' = 'global';
  sortOrder: 'asc' | 'desc' = 'asc';
  alertStates: { [id: string]: { active: boolean; message: string } } = {};
  showAlertModal = false;
  currentAlertParam: any = null;
  alertMessageInput: string = '';
  isSavingAlert = false;

  @ViewChild('alertModalContent', { static: false }) alertModalContent!: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private parametresService: ParametresService,
    private modalClickOutsideService: ModalClickOutsideService
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem("isAdmin") !== "true") {
      this.router.navigate(['/suivi']);
    }

    try {
      const savedFilter = localStorage.getItem('localStorage_SP-GetSettings') as 'global' | 'pre' | 'alert' | null;
      if (savedFilter === 'global' || savedFilter === 'pre' || savedFilter === 'alert') {
        this.selectedParamFilter = savedFilter;
      }
    } catch (e) {
      console.error('Erreur lors de la récupération du filtre SP-GetSettings :', e);
    }
    this.loadParametrages();
  }

  ngAfterViewInit(): void {
    // Initialisation effectuée dans openAlertModal
  }

  ngOnDestroy(): void {
    if (this.alertModalContent) {
      this.modalClickOutsideService.unregisterModal(this.alertModalContent);
    }
  }

  loadParametrages(): void {
    try {
      const savedSort = localStorage.getItem('localStorage_SP-GetSettingsTri') as 'asc' | 'desc' | null;
      if (savedSort === 'asc' || savedSort === 'desc') {
        this.sortOrder = savedSort;
      }
    } catch (e) {
      console.error('Erreur lors de la récupération du tri SP-GetSettingsTri :', e);
    }

    this.isLoading = true;
    this.parametresService.get().subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status) {
          this.parametrages = response.data.parametres;
          this.initializeAllFieldTags();
          this.initializeAlertStates();
        } else {
          showNotif('Impossible de charger les paramètres', 'error');
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error("❌ Erreur SP-GetSettings :", err);
        showNotif('Erreur de chargement', 'error');
      }
    });
  }

  initializeAllFieldTags(): void {
    const tagFields = [4, 5, 6, 8];
    
    tagFields.forEach(fieldNumber => {
      const field = this.parametrages.find(p => p.number === fieldNumber);
      if (field && field.value) {
        this.fieldTags[fieldNumber] = field.value.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0);
      } else {
        this.fieldTags[fieldNumber] = [];
      }
      this.fieldInputs[fieldNumber] = '';
    });
  }

  // Initialise les états d'alerte (active/message) à partir de la valeur brute
  initializeAlertStates(): void {
    this.alertStates = {};
    this.getFilteredParametres('alert').forEach(p => {
      let active = false;
      let message = '';

      if (p.value) {
        try {
          const parsed = JSON.parse(p.value);
          if (parsed && typeof parsed === 'object') {
            active = !!parsed.active;
            message = typeof parsed.message === 'string' ? parsed.message : '';
          } else if (typeof parsed === 'string') {
            message = parsed;
          }
        } catch {
          if (typeof p.value === 'string') {
            message = p.value;
          }
        }
      }

      this.alertStates[p._id] = { active, message };
    });
  }

  addTagIfValid(value: string, fieldNumber: number): void {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    // Validation pour les champs 4, 5, 6 (numéros de téléphone)
    if ([4, 5, 6].includes(fieldNumber)) {
      if (!trimmedValue.startsWith('0')) {
        showNotif('Le numéro doit commencer par 0', 'error');
        this.fieldInputs[fieldNumber] = '';
        return;
      }
      if (trimmedValue.length > 10) {
        showNotif('Le numéro ne peut pas dépasser 10 chiffres', 'error');
        this.fieldInputs[fieldNumber] = '';
        return;
      }
      if (trimmedValue.length < 2) {
        showNotif('Le numéro doit contenir au moins 2 chiffres', 'error');
        this.fieldInputs[fieldNumber] = '';
        return;
      }
    } 
    // Validation pour le champ 8 (minutes 0-59)
    else if (fieldNumber === 8) {
      const numValue = parseInt(trimmedValue, 10);
      if (isNaN(numValue)) {
        showNotif('Veuillez entrer un nombre valide', 'error');
        this.fieldInputs[fieldNumber] = '';
        return;
      }
      if (numValue < 0 || numValue > 59) {
        showNotif('Le nombre doit être compris entre 0 et 59', 'error');
        this.fieldInputs[fieldNumber] = '';
        return;
      }
    }

    if (this.fieldTags[fieldNumber].includes(trimmedValue)) {
      showNotif('Cette valeur est déjà ajoutée', 'error');
      this.fieldInputs[fieldNumber] = '';
      return;
    }

    this.fieldTags[fieldNumber].push(trimmedValue);
    this.fieldInputs[fieldNumber] = '';
    this.updateFieldValue(fieldNumber);
  }

  onFieldInput(event: any, fieldNumber: number): void {
    const value = event.target.value;
    const lastChar = value[value.length - 1];
    
    // Filtrer les caractères non numériques pour les champs tags
    const filteredValue = [4, 5, 6, 8].includes(fieldNumber) 
      ? value.replace(/[^0-9]/g, '') 
      : value;
    this.fieldInputs[fieldNumber] = filteredValue;
    
    if (lastChar === ',') {
      const tagValue = filteredValue.slice(0, -1).trim();
      if (tagValue) {
        this.addTagIfValid(tagValue, fieldNumber);
      } else {
        this.fieldInputs[fieldNumber] = filteredValue.slice(0, -1);
      }
    }
  }

  onFieldKeyDown(event: KeyboardEvent, fieldNumber: number): void {
    if (event.key === 'Enter' && [4, 5, 6, 8].includes(fieldNumber)) {
      event.preventDefault();
      const currentValue = this.fieldInputs[fieldNumber] || '';
      this.addTagIfValid(currentValue, fieldNumber);
      return;
    }
    
    if (event.key === 'Backspace' && this.fieldInputs[fieldNumber] === '' && this.fieldTags[fieldNumber].length > 0) {
      const lastTag = this.fieldTags[fieldNumber].pop();
      this.fieldInputs[fieldNumber] = lastTag || '';
      this.updateFieldValue(fieldNumber);
      event.preventDefault();
    }
  }

  removeTag(index: number, fieldNumber: number): void {
    this.fieldTags[fieldNumber].splice(index, 1);
    this.updateFieldValue(fieldNumber);
  }

  getFilteredParametres(filter: 'global' | 'pre' | 'alert'): any[] {
    const preParametrageNumbers = [1, 2, 9, 10, 11, 12, 13, 14];
    let filtered: any[];

    if (filter === 'global') {
      filtered = this.parametrages.filter(p => 
        !preParametrageNumbers.includes(p.number) && 
        !(p.number >= 15 && p.number <= 20)
      );
    } else if (filter === 'pre') {
      filtered = this.parametrages.filter(p => preParametrageNumbers.includes(p.number));
    } else {
      filtered = this.parametrages.filter(p => p.number >= 15 && p.number <= 20);
    }

    return this.sortByNumber(filtered);
  }

  setParamFilter(filter: 'global' | 'pre' | 'alert'): void {
    if (this.selectedParamFilter !== filter) {
      this.selectedParamFilter = filter;
      try {
        localStorage.setItem('localStorage_SP-GetSettings', filter);
      } catch (e) {
        console.error('Erreur lors de la sauvegarde du filtre SP-GetSettings :', e);
      }
    }
  }

  sortByNumber(params: any[]): any[] {
    return [...params].sort((a, b) => {
      return this.sortOrder === 'asc' ? a.number - b.number : b.number - a.number;
    });
  }

  toggleSort(): void {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    // Sauvegarder le tri dans le localStorage
    try {
      localStorage.setItem('localStorage_SP-GetSettingsTri', this.sortOrder);
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du tri SP-GetSettingsTri :', e);
    }
  }

  // Gestion du changement d'état d'une alerte (activé/désactivé)
  onAlertStateChange(param: any, isActive: boolean): void {
    if (!param || !param._id) {
      return;
    }

    if (!this.alertStates[param._id]) {
      this.alertStates[param._id] = { active: isActive, message: '' };
    } else {
      this.alertStates[param._id].active = isActive;
    }

    // Si on active l'alerte, ouvrir la modale pour saisir/modifier le message
    if (isActive) {
      this.openAlertModal(param);
    }
  }

  openAlertModal(param: any): void {
    if (!param || !param._id) {
      return;
    }
    this.currentAlertParam = param;
    const state = this.alertStates[param._id] || { active: true, message: '' };
    this.alertMessageInput = state.message || '';
    this.showAlertModal = true;
    setTimeout(() => {
      if (this.alertModalContent) {
        this.modalClickOutsideService.registerModal(this.alertModalContent, () => this.closeAlertModal());
      }
    }, 0);
  }

  closeAlertModal(): void {
    if (this.isSavingAlert) {
      return;
    }
    this.showAlertModal = false;
    this.currentAlertParam = null;
    this.alertMessageInput = '';
    if (this.alertModalContent) {
      this.modalClickOutsideService.unregisterModal(this.alertModalContent);
    }
  }

  confirmAlertMessage(): void {
    if (!this.currentAlertParam || !this.currentAlertParam._id) {
      return;
    }

    const param = this.currentAlertParam;
    const id = param._id;

    if (!this.alertStates[id]) {
      this.alertStates[id] = { active: true, message: '' };
    }

    this.alertStates[id].message = (this.alertMessageInput || '').trim();

    const valueToSend = JSON.stringify(this.alertStates[id]);

    this.isSavingAlert = true;
    this.parametresService.update({ id, value: valueToSend }).subscribe({
      next: (response: any) => {
        this.isSavingAlert = false;
        if (response.status) {
          param.value = valueToSend;
          showNotif('Message d\'alerte enregistré', 'success');
        } else {
          showNotif(response.message || 'Erreur lors de l\'enregistrement du message d\'alerte', 'error');
        }
        this.closeAlertModal();
      },
      error: (error: any) => {
        this.isSavingAlert = false;
        console.error('❌ Erreur lors de l\'enregistrement du message d\'alerte :', error);
        showNotif(error?.error?.message || 'Erreur serveur lors de l\'enregistrement du message d\'alerte', 'error');
        this.closeAlertModal();
      }
    });
  }

  updateFieldValue(fieldNumber: number): void {
    const field = this.parametrages.find(p => p.number === fieldNumber);
    if (field) {
      field.value = this.fieldTags[fieldNumber].join(',');
    }
  }

  update(): void {
    this.isLoading = true;
    let completed = 0;
    let total = this.parametrages.length;
    let hasErrors = false;

    // Mettre à jour chaque paramètre
    this.parametrages.forEach((p: any, index: number) => {
      let valueToSend = p.value;

      // Pour les paramètres d'alerte, sérialiser l'état (active/message) en JSON
      if (p.number >= 15 && p.number <= 20 && this.alertStates[p._id]) {
        valueToSend = JSON.stringify(this.alertStates[p._id]);
      }

      const body = { id: p._id, value: valueToSend };
      
      this.parametresService.update(body).subscribe({
        next: (response: any) => {
          completed++;
          
          if (!response.status) {
            hasErrors = true;
            console.error(`Erreur paramètre ${p.number} (${p.name}):`, response.message);
          }
          
          // Quand tous les paramètres ont été traités
          if (completed === total) {
            this.isLoading = false;
            
            if (hasErrors) {
              showNotif('Certains paramètres n\'ont pas pu être mis à jour', 'error');
            } else {
              showNotif('Paramètres mis à jour avec succès', 'success');
            }
          }
        },
        error: (error: any) => {
          completed++;
          hasErrors = true;
          console.error(`Erreur serveur paramètre ${p.number} (${p.name}):`, error);
          
          if (completed === total) {
            this.isLoading = false;
            showNotif('Erreur lors de la mise à jour des paramètres', 'error');
          }
        }
      });
    });
  }
}
